import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Chip, Stack, Tab, Tabs } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { usePublicationCatalog } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import {
  PUBLICATION_CATALOG_STATUS_LABELS,
  PUBLICATION_LIFECYCLE_KIND_LABELS,
  publicationCatalogStatusColor,
} from '../../model/options';
import { PublicationPreviewPanel } from '../components/PublicationPreviewPanel';
import { PublicationRulesPanel } from '../components/PublicationRulesPanel';
import { PublicationRunsPanel } from '../components/PublicationRunsPanel';

type TabKey = 'rules' | 'preview' | 'runs';

/** One publication catalog's Rules / Preview / Runs sub-tabs (tabbed like `PricingPage`). */
export function PublicationCatalogDetailPage() {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const { catalogId = '' } = useParams<{ catalogId: string }>();
  const { canPublicationView, canPublicationManage } = useCatalogPermissions();
  const [tab, setTab] = useState<TabKey>('rules');
  const catalogQuery = usePublicationCatalog(catalogId, { enabled: canPublicationView });

  if (!canPublicationView) {
    return (
      <PageContainer title={t('publicationCatalogDetail.title')}>
        <ErrorState
          title={t('publicationWorkspace.accessRestricted.title')}
          message={t('publicationWorkspace.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  if (catalogQuery.isLoading) {
    return (
      <PageContainer title={t('publicationCatalogDetail.title')}>
        <LoadingState label={t('publicationCatalogDetail.loading')} />
      </PageContainer>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <PageContainer title={t('publicationCatalogDetail.title')}>
        <ErrorState
          title={t('publicationCatalogDetail.loadError.title')}
          message={catalogQuery.error?.message}
          onRetry={() => void catalogQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const catalog = catalogQuery.data;

  return (
    <PageContainer
      title={catalog.name}
      subtitle={catalog.code}
      actions={
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/catalog/publication')}>
          {t('common:action.back')}
        </Button>
      }
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
        <Chip size="small" variant="outlined" label={t(PUBLICATION_LIFECYCLE_KIND_LABELS[catalog.lifecycleKind])} />
        <Chip
          size="small"
          color={publicationCatalogStatusColor(catalog.status)}
          label={t(PUBLICATION_CATALOG_STATUS_LABELS[catalog.status] ?? catalog.status)}
        />
        <Chip size="small" variant="outlined" label={catalog.catalogTypeCode} />
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={tab} onChange={(_e, value: TabKey) => setTab(value)}>
          <Tab value="rules" label={t('publicationCatalogDetail.tabs.rules')} />
          <Tab value="preview" label={t('publicationCatalogDetail.tabs.preview')} />
          <Tab value="runs" label={t('publicationCatalogDetail.tabs.runs')} />
        </Tabs>
      </Box>

      {tab === 'rules' ? (
        <PublicationRulesPanel catalogId={catalog.id} canManage={canPublicationManage} />
      ) : null}
      {tab === 'preview' ? <PublicationPreviewPanel catalogId={catalog.id} /> : null}
      {tab === 'runs' ? <PublicationRunsPanel catalogId={catalog.id} /> : null}
    </PageContainer>
  );
}
